import { useMemo } from "react";
import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { groupRunsIntoSessions, listRecentRuns } from "@/services/multi-agent";
import type { RunHistoryItem, RunSessionHistoryItem } from "@/services/multi-agent";

export const multiAgentRunsKeys = {
  all: ["multi-agent", "runs"] as const,
  list: (orgId: string | null, userId: string | null) =>
    [...multiAgentRunsKeys.all, "list", orgId ?? "", userId ?? ""] as const,
};

function resolveMultiAgentTenantIds(): { orgId: string | null; userId: string | null } {
  const state = useAuthStoreV1.getState();
  return {
    orgId: state.selectedOrg?.id ?? null,
    userId: state.user?.id ?? null,
  };
}

/** Marks the runs list stale and refetches active subscribers (deduped). */
export function invalidateMultiAgentRunsList(queryClient: QueryClient): Promise<void> {
  const { orgId, userId } = resolveMultiAgentTenantIds();
  if (orgId && userId) {
    return queryClient.invalidateQueries({ queryKey: multiAgentRunsKeys.list(orgId, userId) });
  }
  return queryClient.invalidateQueries({ queryKey: multiAgentRunsKeys.all });
}

async function fetchRecentRuns(): Promise<RunHistoryItem[]> {
  const data = await listRecentRuns();
  return data.runs || [];
}

export type UseRecentRunsOptions = {
  enabled?: boolean;
};

export type UseRecentRunsResult = {
  runs: RunHistoryItem[];
  sessions: RunSessionHistoryItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

/**
 * Loads recent multi-agent runs and groups them into sessions.
 */
export function useRecentRuns(options: UseRecentRunsOptions = {}): UseRecentRunsResult {
  const { enabled = true } = options;
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const userId = useAuthStoreV1((s) => s.user?.id ?? null);
  const query = useQuery({
    queryKey: multiAgentRunsKeys.list(orgId, userId),
    queryFn: fetchRecentRuns,
    enabled: enabled && Boolean(orgId && userId),
  });

  const runs = query.data ?? [];
  const sessions = useMemo(() => groupRunsIntoSessions(runs), [runs]);

  return {
    runs,
    sessions,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
